import { parseLapTimeSeconds } from './lapTime';

/**
 * Apex's `gap` / `int` cells carry three different things, verified against a
 * recorded event log:
 *
 *   `""`          — the leader, or not yet known
 *   `"13.350"`    — seconds behind
 *   `"1:00.352"`  — the same, over a minute
 *   `"1 Lap"` / `"12 Laps"` — lapped
 *
 * Lapped entries have no second-value until you pick a pace to convert with,
 * which is why this stays a tagged union rather than collapsing to a number.
 */
export type Gap =
  | { kind: 'seconds'; seconds: number }
  | { kind: 'laps'; laps: number };

export function parseGap(raw: string): Gap | null {
  const value = (raw ?? '').trim();
  if (!value) return null;

  const laps = value.match(/^(\d+)\s+Laps?$/i);
  if (laps) return { kind: 'laps', laps: parseInt(laps[1], 10) };

  // "13.350" and "1:00.352" share the lap-time grammar.
  const seconds = parseLapTimeSeconds(value);
  if (seconds !== null) return { kind: 'seconds', seconds };

  return null;
}

/**
 * Flatten a gap to seconds. A lapped gap needs a pace to convert with, so this
 * returns null when one is not available rather than guessing.
 */
export function gapToSeconds(gap: Gap | null, paceSeconds: number | null): number | null {
  if (!gap) return null;
  if (gap.kind === 'seconds') return gap.seconds;
  if (paceSeconds === null || paceSeconds <= 0) return null;
  return gap.laps * paceSeconds;
}
